import { create } from 'zustand';
import {
  assignSiteCctvMember,
  getSiteCctvMembers,
  revokeSiteCctvMember,
  updateSiteCctvMember,
} from '@/api/siteMembers';
import type {
  SiteCctvMember,
  SiteCctvMemberCreate,
  SiteCctvMemberUpdate,
  SiteCctvRole,
} from '@/types/site-members';

interface SiteMembersState {
  siteId: string | null;
  members: SiteCctvMember[];
  canManageUsers: boolean;
  canManageManagers: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  fetchMembers: (siteId: string) => Promise<void>;
  clearMembers: () => void;
  reset: () => void;
  assignMember: (siteId: string, data: SiteCctvMemberCreate) => Promise<void>;
  updateMember: (
    siteId: string,
    member: SiteCctvMember,
    data: SiteCctvMemberUpdate,
  ) => Promise<void>;
  revokeMember: (siteId: string, member: SiteCctvMember) => Promise<void>;
}

let requestVersion = 0;

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function createSiteMemberRequest(
  identifier: string,
  roleCode: SiteCctvRole,
  endsAt: string | null = null,
): SiteCctvMemberCreate {
  const target = identifier.trim();
  if (uuidPattern.test(target)) return { user_id: target, role_code: roleCode, ends_at: endsAt };
  if (emailPattern.test(target)) return { email: target, role_code: roleCode, ends_at: endsAt };
  throw new Error('등록된 계정의 이메일 또는 계정 ID(UUID)를 입력해주세요.');
}

function assertMemberTarget(data: SiteCctvMemberCreate) {
  const hasEmail = data.email !== undefined;
  const hasUserId = data.user_id !== undefined;
  if (hasEmail === hasUserId) throw new Error('이메일 또는 계정 ID 중 하나만 지정해주세요.');
  if (hasEmail && (typeof data.email !== 'string' || !emailPattern.test(data.email))) {
    throw new Error('올바른 이메일을 입력해주세요.');
  }
  if (hasUserId && (typeof data.user_id !== 'string' || !uuidPattern.test(data.user_id))) {
    throw new Error('계정 ID는 통합 정보 관리단의 UUID여야 합니다.');
  }
}

const emptyState = {
  siteId: null,
  members: [],
  canManageUsers: false,
  canManageManagers: false,
  isLoading: false,
  isSaving: false,
  error: null,
};

export const useSiteMembersStore = create<SiteMembersState>((set, get) => {
  const reset = () => {
    ++requestVersion;
    set({ ...emptyState });
  };
  const assertCanManage = (siteId: string, roles: SiteCctvRole[]) => {
    const state = get();
    if (state.siteId !== siteId || state.isLoading || state.isSaving || state.error) {
      throw new Error('현장 구성원 정보를 새로 확인한 후 다시 시도해주세요.');
    }
    const allowed = roles.every((role) =>
      role === 'site_manager' ? state.canManageManagers : state.canManageUsers,
    );
    if (!allowed) throw new Error('이 현장의 해당 역할을 관리할 권한이 없습니다.');
  };

  const mutate = async (siteId: string, operation: () => Promise<unknown>) => {
    const version = requestVersion;
    set({ isSaving: true });
    try {
      await operation();
    } catch (error) {
      if (get().siteId === siteId && requestVersion === version) await get().fetchMembers(siteId);
      throw error;
    } finally {
      if (get().siteId === siteId && requestVersion === version) set({ isSaving: false });
    }
    if (get().siteId === siteId && requestVersion === version) await get().fetchMembers(siteId);
  };

  return {
    ...emptyState,
    fetchMembers: async (siteId) => {
      const version = ++requestVersion;
      set({ ...emptyState, siteId, isLoading: true });
      try {
        const response = await getSiteCctvMembers(siteId);
        if (version !== requestVersion) return;
        set({
          members: response.items,
          canManageUsers: response.can_manage_users === true,
          canManageManagers: response.can_manage_managers === true,
          isLoading: false,
        });
      } catch (error) {
        if (version !== requestVersion) return;
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : '현장 구성원을 불러오지 못했습니다.',
        });
      }
    },
    clearMembers: reset,
    reset,
    assignMember: async (siteId, data) => {
      assertCanManage(siteId, [data.role_code]);
      assertMemberTarget(data);
      await mutate(siteId, () => assignSiteCctvMember(siteId, data));
    },
    updateMember: async (siteId, member, data) => {
      assertCanManage(siteId, [member.role_code, data.role_code]);
      if (
        !get().members.some(
          (item) => item.user_id === member.user_id && item.version === member.version,
        )
      ) {
        throw new Error('구성원 정보가 변경되었습니다. 새로고침 후 다시 시도해주세요.');
      }
      await mutate(siteId, () => updateSiteCctvMember(siteId, member.user_id, data));
    },
    revokeMember: async (siteId, member) => {
      assertCanManage(siteId, [member.role_code]);
      if (
        !get().members.some(
          (item) => item.user_id === member.user_id && item.version === member.version,
        )
      ) {
        throw new Error('구성원 정보가 변경되었습니다. 새로고침 후 다시 시도해주세요.');
      }
      await mutate(siteId, () => revokeSiteCctvMember(siteId, member.user_id, member.version));
    },
  };
});
